import { useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useRouterState,
  type SearchSchemaInput,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DashboardPage } from "@/components/reporting/dashboards/dashboard-page";
import {
  filtersFromSearch,
  parseDashboardSearch,
  searchFromFilters,
  type DashboardSearch,
} from "@/components/reporting/dashboards/viewer-filters";
import { useAppStore } from "@/store";

/**
 * The one channel the new-dashboard landing invites arranging through. A
 * dashboard created a moment ago has no tiles, so it opens with the arrange
 * surface and the add panel already up.
 *
 * It is an INVITATION, and an invitation is spent when it is taken: the route
 * reads the flag on arrival and immediately replaces the history entry without
 * it. Chromium restores `history.state` across a reload — and a navigation to
 * the URL already on screen IS a reload — so a flag left on the entry would
 * re-open the arrange surface every time a coordinator refreshed a board they
 * had just saved. Once spent, a reload lands in view mode, which is right.
 */
declare module "@tanstack/react-router" {
  interface HistoryState {
    dashboardArrange?: boolean;
  }
}

/**
 * `SearchSchemaInput` keeps the INPUT half optional while the output stays the
 * validated set, so every `to: "/reports/dashboards/$id"` — the list row, the
 * create landing — navigates without spelling filters it has no opinion about.
 */
type DashboardSearchInput = Record<string, unknown> & SearchSchemaInput;

export const Route = createFileRoute("/_authenticated/reports/dashboards/$id/")({
  component: ReportDashboardRoute,
  validateSearch: (search: DashboardSearchInput): DashboardSearch => parseDashboardSearch(search),
});

/**
 * One dashboard, run. The page owns the read, the run, the filters' effect, the
 * tiles, the drill, arranging and the delete; the route owns the page gutter,
 * the filters the URL carries, and where each of the page's exits leads. It is
 * the SHARED surface of the two the page draws: a way back to the list, and a
 * way on to the home the set-home toast offers.
 *
 * THE FILTERS ARE THE URL'S. A viewer's period, premises and comparison ride
 * the search params, so the reading a coordinator is looking at is a link they
 * can send; an unset member drops out rather than riding as an empty value, and
 * every write replaces, so history keeps one entry per visit rather than one
 * per click — the `?tab=` precedent two entities up.
 */
function ReportDashboardRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const { t } = useTranslation("org");
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);
  const invited = useRouterState({
    select: (state) => state.location.state.dashboardArrange === true,
  });
  // Frozen at mount, so the page's opening mode does not depend on when the
  // entry below is rewritten.
  const [landedArranging] = useState(invited);

  // Spend the invitation. Everything about the entry stays — the path, the
  // params, the search and the history depth — and only the flag goes, so a
  // reload of this same URL comes back in view mode.
  useEffect(() => {
    if (!invited) return;
    void navigate({
      to: ".",
      params: true,
      search: true,
      replace: true,
      state: (previous) => {
        const { dashboardArrange: _spent, ...rest } = previous;
        return rest;
      },
    });
  }, [invited, navigate]);

  // Held on the search object: the page keys its injected instant — and through
  // it the whole run's overrides — on this object's identity, so a fresh one
  // per render would re-derive the run on every unrelated render.
  const filters = useMemo(() => filtersFromSearch(search), [search]);

  const backToDashboards = () => {
    void navigate({ to: "/reports", search: { tab: "dashboards" } });
  };

  // Where the set-home toast's "Go to Home" leads. The page raises the toast;
  // only the route knows the destination.
  const goHome = () => {
    void navigate({ to: "/" });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext")}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <DashboardPage
        orgId={orgId}
        dashboardId={id}
        filters={filters}
        onFiltersChange={(next) => {
          void navigate({
            to: "/reports/dashboards/$id",
            params: { id },
            // Spread because TypeScript grants an implicit index signature —
            // which is what the router's search parameter asks for — to
            // anonymous object types but not to interfaces, and
            // `consistent-type-definitions` makes `DashboardSearch` an
            // interface.
            search: { ...searchFromFilters(next) },
            replace: true,
          });
        }}
        surface={{ kind: "shared", onBack: backToDashboards, onGoHome: goHome }}
        onDeleted={backToDashboards}
        onOpenReport={(reportId) => {
          void navigate({ to: "/reports/$id", params: { id: reportId } });
        }}
        onEditReport={(reportId) => {
          void navigate({ to: "/reports/$id/edit", params: { id: reportId } });
        }}
        onOpenAsReport={(derived) => {
          void navigate({
            to: "/reports/new",
            state: {
              reportSeed: { query: derived, visualization: { schema_version: 1, kind: "table" } },
            },
          });
        }}
        initialArranging={landedArranging}
        initialPanelOpen={landedArranging}
      />
    </div>
  );
}
